import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  Users,
  DollarSign,
  ShoppingCart,
  Activity,
  FolderKanban,
  Receipt,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AnalyticsHeader } from "@/components/analytics/AnalyticsHeader";
import { StatCard } from "@/components/analytics/StatCard";
import { ChartCard } from "@/components/analytics/ChartCard";
import { RecentActivity } from "@/components/analytics/RecentActivity";
import { TrafficAnalytics } from "@/components/analytics/TrafficAnalytics";
import { useCurrency } from "@/hooks/use-currency";
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, subMonths } from "date-fns";
import { useMemo } from "react";
import { useRealtimeSubscription } from "@/hooks/use-realtime";

const Analytics = () => {
  const { formatAmount, convertAmount, currency } = useCurrency();

  // Enable realtime updates for all data sources
  useRealtimeSubscription("subscriptions", "subscriptions-analytics");
  useRealtimeSubscription("invoices", "invoices-analytics");
  useRealtimeSubscription("projects", "projects-analytics");
  useRealtimeSubscription("customers", "clients-analytics");
  useRealtimeSubscription("profiles", "signup-clients-analytics");
  useRealtimeSubscription("service_requests", "service-request-clients-analytics");
  useRealtimeSubscription("business_expenses", "expenses-analytics");
  useRealtimeSubscription("service_requests", "service-requests-analytics");

  // Fetch all data
  const { data: subscriptions } = useQuery({
    queryKey: ["subscriptions-analytics"],
    queryFn: async () => {
      const { data, error } = await supabase.from("subscriptions").select("*");
      if (error) throw error;
      return data;
    },
  });

  const { data: invoices } = useQuery({
    queryKey: ["invoices-analytics"],
    queryFn: async () => {
      const { data, error } = await supabase.from("invoices").select("*");
      if (error) throw error;
      return data;
    },
  });

  const { data: projects } = useQuery({
    queryKey: ["projects-analytics"],
    queryFn: async () => {
      const { data, error } = await supabase.from("projects").select("*");
      if (error) throw error;
      return data;
    },
  });

  // Fetch all client sources
  const { data: manualClients } = useQuery({
    queryKey: ["clients-analytics"],
    queryFn: async () => {
      const { data, error } = await supabase.from("customers").select("*");
      if (error) throw error;
      return data;
    },
  });

  const { data: signupClients } = useQuery({
    queryKey: ["signup-clients-analytics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_type", "client");
      if (error) throw error;
      return data;
    },
  });

  const { data: serviceRequestClients } = useQuery({
    queryKey: ["service-request-clients-analytics"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_requests")
        .select("*");
      if (error) throw error;
      
      // Get unique clients by email
      const uniqueClients = new Map();
      data?.forEach(request => {
        if (!uniqueClients.has(request.email)) {
          uniqueClients.set(request.email, request);
        }
      });
      
      return Array.from(uniqueClients.values());
    },
  });

  // Combine all client sources and remove duplicates
  const clients = useMemo(() => {
    const clientMap = new Map();
    
    // Add manual clients
    manualClients?.forEach(client => {
      if (client.email) {
        clientMap.set(client.email.toLowerCase(), client);
      } else {
        clientMap.set(`manual-${client.id}`, client);
      }
    });
    
    // Add signup clients if not already added
    signupClients?.forEach(client => {
      if (client.email && !clientMap.has(client.email.toLowerCase())) {
        clientMap.set(client.email.toLowerCase(), client);
      }
    });
    
    // Add service request clients if not already added
    serviceRequestClients?.forEach(client => {
      if (client.email && !clientMap.has(client.email.toLowerCase())) {
        clientMap.set(client.email.toLowerCase(), client);
      }
    });
    
    return Array.from(clientMap.values());
  }, [manualClients, signupClients, serviceRequestClients]);

  const { data: expenses } = useQuery({
    queryKey: ["expenses-analytics"],
    queryFn: async () => {
      const { data, error } = await supabase.from("business_expenses").select("*");
      if (error) throw error;
      return data;
    },
  });

  const { data: serviceRequests } = useQuery({
    queryKey: ["service-requests-analytics"],
    queryFn: async () => {
      const { data, error } = await supabase.from("service_requests").select("*");
      if (error) throw error;
      return data;
    },
  });

  // Calculate metrics
  // Calculate key metrics - convert all amounts to display currency (default to USD if currency not set)
  // Include both subscriptions and paid invoices (exclude invoice-type subscriptions to avoid double counting)
  const subscriptionRevenue = subscriptions
    ?.filter((sub) => sub.payment_type !== 'invoice')
    .reduce((acc, sub) => {
      const sourceCurrency = (sub.currency as any) || "USD";
      const converted = convertAmount(Number(sub.price), sourceCurrency, currency);
      return acc + converted;
    }, 0) || 0;
  
  const invoiceRevenue = invoices?.filter(inv => inv.status !== 'draft' && inv.status !== 'cancelled')
    .reduce((acc, inv) => {
      const sourceCurrency = (inv.currency as any) || "USD";
      const converted = convertAmount(Number(inv.total_amount), sourceCurrency, currency);
      return acc + converted;
    }, 0) || 0;
  
  const totalRevenue = subscriptionRevenue + invoiceRevenue;
  
  const totalExpenses = expenses?.reduce((acc, exp) => {
    const sourceCurrency = (exp.currency as any) || "USD";
    const converted = convertAmount(Number(exp.amount), sourceCurrency, currency);
    return acc + converted;
  }, 0) || 0;
  
  const netProfit = totalRevenue - totalExpenses;
  const activeProjects = projects?.filter(p => p.status === "active").length || 0;
  const totalClients = clients?.length || 0;
  const pendingRequests = serviceRequests?.filter(r => r.status === "pending").length || 0;

  // Prepare monthly revenue data (last 6 months)
  const last6Months = eachMonthOfInterval({
    start: subMonths(new Date(), 5),
    end: new Date()
  });

  const monthlyRevenueData = last6Months.map(month => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    
    // Revenue from subscriptions (exclude invoice-type to avoid double counting)
    const monthSubscriptionRevenue = subscriptions
      ?.filter(sub => {
        const subDate = new Date(sub.created_at || "");
        return sub.payment_type !== 'invoice' && subDate >= monthStart && subDate <= monthEnd;
      })
      .reduce((sum, sub) => {
        const sourceCurrency = (sub.currency as any) || "USD";
        const converted = convertAmount(Number(sub.price), sourceCurrency, currency);
        return sum + converted;
      }, 0) || 0;
    
    // Revenue from invoices (exclude draft/cancelled)
    const monthInvoiceRevenue = invoices?.filter(inv => {
      const invDate = new Date(inv.created_at || "");
      return (inv.status !== 'draft' && inv.status !== 'cancelled') && invDate >= monthStart && invDate <= monthEnd;
    }).reduce((sum, inv) => {
      const sourceCurrency = (inv.currency as any) || "USD";
      const converted = convertAmount(Number(inv.total_amount), sourceCurrency, currency);
      return sum + converted;
    }, 0) || 0;
    
    const monthRevenue = monthSubscriptionRevenue + monthInvoiceRevenue;

    const monthExpenses = expenses?.filter(exp => {
      const expDate = new Date(exp.created_at);
      return expDate >= monthStart && expDate <= monthEnd;
    }).reduce((sum, exp) => {
      const sourceCurrency = (exp.currency as any) || "USD";
      const converted = convertAmount(Number(exp.amount), sourceCurrency, currency);
      return sum + converted;
    }, 0) || 0;

    return {
      name: format(month, "MMM"),
      revenue: monthRevenue,
      expenses: monthExpenses,
      profit: monthRevenue - monthExpenses
    };
  });

  // Project status distribution
  const projectStatusData = [
    { 
      name: "Active", 
      value: projects?.filter(p => p.status === "active").length || 0,
      color: "hsl(var(--primary))" 
    },
    { 
      name: "Planning", 
      value: projects?.filter(p => p.status === "planning").length || 0,
      color: "hsl(var(--accent))" 
    },
    { 
      name: "Completed", 
      value: projects?.filter(p => p.status === "completed").length || 0,
      color: "hsl(var(--secondary))" 
    },
    { 
      name: "On Hold", 
      value: projects?.filter(p => p.status === "on-hold").length || 0,
      color: "hsl(var(--muted))" 
    },
  ].filter(item => item.value > 0);

  // Expense categories - convert to display currency (default to USD if currency not set)
  const expensesByCategory = expenses?.reduce((acc, exp) => {
    const category = exp.category || "other";
    const sourceCurrency = (exp.currency as any) || "USD";
    const converted = convertAmount(Number(exp.amount), sourceCurrency, currency);
    acc[category] = (acc[category] || 0) + converted;
    return acc;
  }, {} as Record<string, number>) || {};

  const expenseCategoryData = Object.entries(expensesByCategory).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));

  return (
    <div className="space-y-6 animate-fade-in">
      <AnalyticsHeader />

      {/* Key Metrics */}
      <div className="grid gap-6 md:grid-cols-4">
        <StatCard
          title="Total Revenue"
          value={formatAmount(totalRevenue, currency)}
          icon={DollarSign}
          progress={75}
          trend={{ value: 12.5, isPositive: true }}
        />
        <StatCard
          title="Net Profit"
          value={formatAmount(netProfit, currency)}
          icon={TrendingUp}
          iconContainerClassName={`p-4 rounded-full ${netProfit >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}
          iconClassName={`h-6 w-6 ${netProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
          progress={netProfit >= 0 ? 80 : 20}
          trend={{ value: 8.2, isPositive: netProfit >= 0 }}
        />
        <StatCard
          title="Active Projects"
          value={activeProjects}
          icon={FolderKanban}
          iconContainerClassName="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full"
          iconClassName="h-6 w-6 text-blue-600 dark:text-blue-400"
          progress={60}
          trend={{ value: 5.1, isPositive: true }}
        />
        <StatCard
          title="Total Clients"
          value={totalClients}
          icon={Users}
          iconContainerClassName="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full"
          iconClassName="h-6 w-6 text-purple-600 dark:text-purple-400"
          progress={45}
          trend={{ value: 3.2, isPositive: true }}
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid gap-6 md:grid-cols-3">
        <StatCard
          title="Total Expenses"
          value={formatAmount(totalExpenses)}
          icon={Receipt}
          iconContainerClassName="p-4 bg-red-100 dark:bg-red-900/30 rounded-full"
          iconClassName="h-6 w-6 text-red-600 dark:text-red-400"
          progress={35}
          trend={{ value: 2.1, isPositive: false }}
        />
        <StatCard
          title="Subscriptions"
          value={subscriptions?.length || 0}
          icon={ShoppingCart}
          iconContainerClassName="p-4 bg-orange-100 dark:bg-orange-900/30 rounded-full"
          iconClassName="h-6 w-6 text-orange-600 dark:text-orange-400"
          progress={55}
          trend={{ value: 4.5, isPositive: true }}
        />
        <StatCard
          title="Pending Requests"
          value={pendingRequests}
          icon={Activity}
          iconContainerClassName="p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-full"
          iconClassName="h-6 w-6 text-yellow-600 dark:text-yellow-400"
          progress={40}
          trend={{ value: 1.2, isPositive: false }}
        />
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <ChartCard title="Revenue & Expenses Overview" subtitle="Monthly comparison">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthlyRevenueData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="hsl(var(--primary))"
                fillOpacity={1}
                fill="url(#colorRevenue)"
              />
              <Area
                type="monotone"
                dataKey="expenses"
                stroke="hsl(var(--destructive))"
                fillOpacity={1}
                fill="url(#colorExpenses)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Profit Trend" subtitle="Net profit over time">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyRevenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="profit"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ fill: "hsl(var(--primary))" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Project Status Distribution" subtitle="Current project breakdown">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={projectStatusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => entry.name}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {projectStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Expense Categories" subtitle="Breakdown by category">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={expenseCategoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="mt-8">
        <h2 className="text-2xl font-bold mb-4">Website Traffic</h2>
        <TrafficAnalytics />
      </div>

      <div className="mt-8">
        <RecentActivity />
      </div>
    </div>
  );
};

export default Analytics;